import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCheck,
  faClose,
  faCode,
  faDownload,
  faFileImport,
  faTable,
  faUpload,
} from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import ModalPortal from "../Modal/ModalPortal"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../Titles/Titles"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { ButtonIcon } from "../Button/Button"
import { PopupHelpButton } from "../Help/PopupHelpButton"
import type { DataImportFormat } from "../../Objects/DataImport/DataImportFormat"
import type { DataImportTarget } from "../../Objects/DataImport/DataImportTarget"
import type { DataImportFieldDefinition } from "../../Objects/DataImport/DataImportFieldDefinition"
import type { DataImportPreview } from "../../Objects/DataImport/DataImportPreview"
import { useDataImport } from "../../hooks/DataImport/useDataImport"
import { useConfirmation } from "../../context/useConfirmationContext"

interface ImportDataPopupProps {
  target: DataImportTarget
  title: string
  onClose: () => void
  onImported: () => void
}

const formats: DataImportFormat[] = ["csv", "xlsx", "json"]

const formatButtonBaseClass =
  "inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-bold transition-colors"

const actionButtonBaseClass =
  "inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-55"

const previewGridClass =
  "grid min-w-[720px] grid-cols-[96px_72px_minmax(120px,1fr)_92px_minmax(160px,1.5fr)] items-start gap-2"

export const ImportDataPopup: React.FC<ImportDataPopupProps> = ({ target, title, onClose, onImported }) => {
  const { t } = useTranslation("dataImport")

  const inputRef = useRef<HTMLInputElement | null>(null)

  const [format, setFormat] = useState<DataImportFormat>("csv")

  const [file, setFile] = useState<File | null>(null)

  const [preview, setPreview] = useState<DataImportPreview | null>(null)

  const [fields, setFields] = useState<DataImportFieldDefinition[]>([])

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())

  const [isDraggingFile, setIsDraggingFile] = useState(false)

  const { handleGetSchema, handleDownloadTemplate, handleValidateImport, handleCommitImport } = useDataImport()

  const { setNewConfirmation } = useConfirmation()

  const canImport = !!preview && selectedRows.size > 0

  const previewRows = useMemo(() => preview?.rows ?? [], [preview])

  const validRowNumbers = useMemo(
    () => preview?.rows.filter((row) => row.isValid).map((row) => row.rowNumber) ?? [],
    [preview],
  )

  const allValidRowsSelected =
    validRowNumbers.length > 0 && validRowNumbers.every((rowNumber) => selectedRows.has(rowNumber))

  useEffect(() => {
    handleGetSchema(target, (schema) => setFields(schema.fields))
  }, [target])

  const getFormatLabel = (value: DataImportFormat) => {
    switch (value) {
      case "json":
        return t("format_json")
      case "xlsx":
        return t("format_xlsx")
      default:
        return t("format_csv")
    }
  }

  const selectFile = (selectedFile: File | null) => {
    setFile(selectedFile)

    setPreview(null)

    setSelectedRows(new Set())
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0] ?? null)
  }

  const handleValidate = () => {
    handleValidateImport(target, file, (result) => {
      setPreview(result)
      setFields(result.fields)
      setSelectedRows(new Set(result.rows.filter((row) => row.isValid).map((row) => row.rowNumber)))
    })
  }

  const commitImport = () => {
    handleCommitImport(target, file, [...selectedRows], () => {
      onClose()
      onImported()
    })
  }

  const handleImport = () => {
    setNewConfirmation(
      t("confirmation_title"),
      t("confirmation_description", { count: selectedRows.size }),
      commitImport,
    )
  }

  const handleToggleRow = (rowNumber: number) => {
    setSelectedRows((currentRows) => {
      const nextRows = new Set(currentRows)

      if (nextRows.has(rowNumber)) {
        nextRows.delete(rowNumber)
      } else {
        nextRows.add(rowNumber)
      }

      return nextRows
    })
  }

  const handleToggleAllValidRows = () => {
    setSelectedRows(allValidRowsSelected ? new Set() : new Set(validRowNumbers))
  }

  const handleDropFile = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault()
    setIsDraggingFile(false)
    selectFile(event.dataTransfer.files?.[0] ?? null)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faFileImport}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} gap-1`}>
                  <PopupHelpButton
                    helpKey="components.dataimport.importdatapopup.1"
                    variant="global"
                    title={t("helpPopup:global.dataImport.title")}
                    description={t("helpPopup:global.dataImport.description")}
                    bullets={t("helpPopup:global.dataImport.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:components.dataImport.importDataPopup.button.onClose"
                  />
                </div>
              }
            />

            <div className="flex flex-col gap-3.5 p-2.5">
              {previewRows.length === 0 && (
                <section className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {formats.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={`${formatButtonBaseClass} ${
                        format === item ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                      }`}
                      onClick={() => setFormat(item)}
                      data-interaction-description-key="interactionDescriptions:components.dataImport.importDataPopup.button.setFormatItem"
                    >
                      <FontAwesomeIcon icon={item === "json" ? faCode : faTable} />
                      {getFormatLabel(item)}
                    </button>
                  ))}
                </section>
              )}

              {previewRows.length === 0 && (
                <section className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={`${actionButtonBaseClass} bg-blue-600 hover:bg-blue-700`}
                    onClick={() => handleDownloadTemplate(target, format)}
                    data-interaction-description-key="interactionDescriptions:components.dataImport.importDataPopup.button.handleDownloadTemplateTargetFormat"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    {t("button_download_template", { format: getFormatLabel(format) })}
                  </button>
                </section>
              )}

              {previewRows.length === 0 && (
                <>
                  <section className="grid max-h-56 grid-cols-1 gap-2 overflow-auto md:grid-cols-2">
                    {fields.map((field) => (
                      <div key={field.name} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                        <div className="flex items-center gap-1.5 text-sm font-extrabold text-slate-900">
                          <FontAwesomeIcon icon={field.required ? faCheck : faTable} />

                          <span>{field.name}</span>
                        </div>

                        <div className="mt-1 text-xs text-slate-600">{field.description}</div>

                        {field.example && (
                          <div className="mt-1 break-words text-xs text-slate-500">
                            {t("label_field_example", { example: field.example })}
                          </div>
                        )}
                      </div>
                    ))}
                  </section>

                  <section
                    className={`flex flex-col gap-2.5 rounded-lg border border-dashed p-3.5 transition-colors ${
                      isDraggingFile ? "border-blue-600 bg-blue-50" : "border-slate-400 bg-slate-50"
                    }`}
                    onDragEnter={(event) => {
                      event.preventDefault()
                      setIsDraggingFile(true)
                    }}
                    onDragOver={(event) => event.preventDefault()}
                    onDragLeave={() => setIsDraggingFile(false)}
                    onDrop={handleDropFile}
                  >
                    <input
                      ref={inputRef}
                      className="hidden"
                      type="file"
                      accept=".csv,.xlsx,.json,.txt"
                      onChange={handleFileChange}
                    />

                    <button
                      type="button"
                      className={`${formatButtonBaseClass} bg-gray-100 text-gray-900 hover:bg-gray-200`}
                      onClick={() => inputRef.current?.click()}
                      data-interaction-description-key="interactionDescriptions:components.dataImport.importDataPopup.button.inputRefCurrent"
                    >
                      <FontAwesomeIcon icon={faUpload} />
                      {t("button_select_file")}
                    </button>

                    <span className="text-xs font-bold text-slate-500">{t("label_drop_file")}</span>

                    <span className="break-words text-sm text-slate-700">
                      {file ? file.name : t("label_no_file_selected")}
                    </span>
                  </section>
                </>
              )}

              {preview && (
                <>
                  <section className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <div className="flex flex-col gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                      <span className="text-xs font-bold text-slate-500">{t("label_total_rows")}</span>
                      <span className="text-lg font-extrabold text-slate-900">{preview.totalRows}</span>
                    </div>

                    <div className="flex flex-col gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                      <span className="text-xs font-bold text-slate-500">{t("label_valid_rows")}</span>
                      <span className="text-lg font-extrabold text-slate-900">{preview.validRows}</span>
                    </div>

                    <div className="flex flex-col gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
                      <span className="text-xs font-bold text-slate-500">{t("label_invalid_rows")}</span>
                      <span className="text-lg font-extrabold text-slate-900">{preview.invalidRows}</span>
                    </div>
                  </section>

                  <section className="flex justify-end text-sm font-extrabold text-slate-700">
                    <span>{t("label_selected_rows", { count: selectedRows.size })}</span>
                  </section>

                  <section className="overflow-x-auto rounded-lg border border-slate-200">
                    <div className={`${previewGridClass} bg-slate-900 px-2.5 py-2 text-xs font-extrabold text-white`}>
                      <label className="inline-flex items-center gap-1.5 font-extrabold">
                        <input
                          className="h-4 w-4 accent-blue-600"
                          type="checkbox"
                          checked={allValidRowsSelected}
                          disabled={validRowNumbers.length === 0}
                          aria-label={t("aria_select_all_valid_rows")}
                          onChange={handleToggleAllValidRows}
                          data-interaction-description-key="interactionDescriptions:components.dataImport.importDataPopup.input.ariaSelectAllValidRows"
                        />
                        <span>{t("column_import")}</span>
                      </label>
                      <span>{t("column_row")}</span>
                      <span>{t("column_identifier")}</span>
                      <span>{t("column_status")}</span>
                      <span>{t("column_errors")}</span>
                    </div>

                    {previewRows.map((row) => (
                      <div
                        key={`${row.rowNumber}-${row.identifier}`}
                        className={`${previewGridClass} border-t border-slate-200 px-2.5 py-2 text-sm ${
                          row.isValid ? "bg-white" : "bg-rose-50"
                        }`}
                      >
                        <label className="inline-flex items-center gap-1.5 font-extrabold">
                          <input
                            className="h-4 w-4 accent-blue-600"
                            type="checkbox"
                            checked={row.isValid && selectedRows.has(row.rowNumber)}
                            disabled={!row.isValid}
                            aria-label={t("aria_select_row", { row: row.rowNumber })}
                            onChange={() => handleToggleRow(row.rowNumber)}
                            data-interaction-description-key="interactionDescriptions:components.dataImport.importDataPopup.input.ariaSelectRow"
                          />
                        </label>
                        <span>{row.rowNumber}</span>
                        <span>{row.identifier}</span>
                        <span
                          className={row.isValid ? "font-extrabold text-emerald-700" : "font-extrabold text-rose-700"}
                        >
                          {row.isValid ? t("status_valid") : t("status_invalid")}
                        </span>
                        <div className="flex flex-col gap-1 text-rose-700">
                          {row.errors.length === 0 ? (
                            <span>{t("label_no_errors")}</span>
                          ) : (
                            row.errors.map((error) => <span key={error}>{error}</span>)
                          )}
                        </div>
                      </div>
                    ))}
                  </section>
                </>
              )}

              <section className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  className={`${actionButtonBaseClass} bg-green-600 hover:bg-green-700`}
                  onClick={handleValidate}
                  disabled={!file}
                  data-interaction-description-key="interactionDescriptions:components.dataImport.importDataPopup.button.handleValidate"
                >
                  <FontAwesomeIcon icon={faCheck} />
                  {t("button_validate")}
                </button>

                <button
                  type="button"
                  className={`${actionButtonBaseClass} bg-green-600 hover:bg-green-700`}
                  onClick={handleImport}
                  disabled={!canImport}
                  data-interaction-description-key="interactionDescriptions:components.dataImport.importDataPopup.button.handleImport"
                >
                  <FontAwesomeIcon icon={faFileImport} />
                  {t("button_import")}
                </button>

                <button
                  type="button"
                  className={`${actionButtonBaseClass} bg-red-600 hover:bg-red-700`}
                  onClick={onClose}
                  data-interaction-description-key="interactionDescriptions:components.dataImport.importDataPopup.button.onClose2"
                >
                  <FontAwesomeIcon icon={faClose} />
                  {t("buttons:button_cancel")}
                </button>
              </section>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
